import React from "react";
import { useI18n } from "../i18n";
import ContactForm from "../components/ContactForm";
import {
  BarChart3,
  Layers,
  Wallet,
  Globe2,
  Search,
  Target,
  LineChart,
  ClipboardCheck,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";

const IMG = {
  hero: "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1600&q=80",
  salar: "https://images.unsplash.com/photo-1610555356070-d0efb6505f81?auto=format&fit=crop&w=1200&q=80",
  rig: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&w=1200&q=80",
  mining: "https://images.unsplash.com/photo-1508615070457-7baeba4003ab?auto=format&fit=crop&w=1200&q=80",
  dashboard: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
  city: "https://images.unsplash.com/photo-1516937941344-00b4e0337589?auto=format&fit=crop&w=1600&q=80",
};

export default function Home() {
  const { t } = useI18n();

  const smoothTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, "", `#${id}`);
    }
  };

  return (
    <main id="main-content" data-testid="page-home">
      {/* HERO */}
      <section className="hero" id="top">
        <div className="container hero__grid">
          <div className="hero__copy">
            <span className="eyebrow">{t("hero.eyebrow")}</span>
            <h1 className="hero__title">{t("hero.title")}</h1>
            <p className="hero__lead">{t("hero.subtitle")}</p>
            <p className="hero__body">{t("hero.description")}</p>
            <div className="hero__cta">
              <a href="#contact" className="btn btn--primary" onClick={(e) => smoothTo(e, "contact")} data-testid="hero-cta-primary">
                {t("hero.ctaPrimary")}
                <ArrowRight size={16} aria-hidden="true" />
              </a>
              <a href="#process" className="btn btn--ghost" onClick={(e) => smoothTo(e, "process")} data-testid="hero-cta-secondary">
                {t("hero.ctaSecondary")}
              </a>
            </div>
            <ul className="hero__stats">
              <li>
                <strong>{t("hero.stat1.value")}</strong>
                <span>{t("hero.stat1.label")}</span>
              </li>
              <li>
                <strong>{t("hero.stat2.value")}</strong>
                <span>{t("hero.stat2.label")}</span>
              </li>
              <li>
                <strong>{t("hero.stat3.value")}</strong>
                <span>{t("hero.stat3.label")}</span>
              </li>
            </ul>
          </div>
          <div className="hero__media" aria-hidden="true">
            <div className="hero__frame">
              <img src={IMG.hero} alt="" loading="eager" />
              <div className="hero__badge">
                <LineChart size={18} />
                <div>
                  <span>Salar de Olaroz</span>
                  <strong>Li₂CO₃ · US$ 14.820 / t</strong>
                </div>
              </div>
              <div className="hero__badge hero__badge--alt">
                <BarChart3 size={18} />
                <div>
                  <span>Vaca Muerta</span>
                  <strong>Brent · US$ 79.4 / bbl</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="section" id="overview">
        <div className="container">
          <div className="section__header">
            <span className="eyebrow">{t("nav.overview")}</span>
            <h2>{t("why.title")}</h2>
            <p>{t("why.lead")}</p>
          </div>
          <div className="why-grid">
            {[
              { icon: <Search size={20} />, key: "info" },
              { icon: <Layers size={20} />, key: "digital" },
              { icon: <Wallet size={20} />, key: "variety" },
              { icon: <Globe2 size={20} />, key: "impact" },
            ].map(({ icon, key }) => (
              <article className="why-card" key={key}>
                <div className="why-card__icon" aria-hidden="true">{icon}</div>
                <h3>{t(`why.items.${key}.title`)}</h3>
                <p>{t(`why.items.${key}.text`)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="section section--muted">
        <div className="container">
          <div className="section__header">
            <h2>{t("sectors.title")}</h2>
            <p>{t("sectors.lead")}</p>
          </div>
          <div className="sectors-grid">
            <article className="sector-card">
              <div className="sector-card__media"><img src={IMG.salar} alt="Salar de Jujuy, Argentina" loading="lazy" /></div>
              <div className="sector-card__body">
                <span className="tag">{t("sectors.lithium.tag")}</span>
                <h3>{t("sectors.lithium.title")}</h3>
                <p>{t("sectors.lithium.text")}</p>
              </div>
            </article>
            <article className="sector-card">
              <div className="sector-card__media"><img src={IMG.rig} alt="Vaca Muerta drilling rig" loading="lazy" /></div>
              <div className="sector-card__body">
                <span className="tag">{t("sectors.vaca.tag")}</span>
                <h3>{t("sectors.vaca.title")}</h3>
                <p>{t("sectors.vaca.text")}</p>
              </div>
            </article>
            <article className="sector-card">
              <div className="sector-card__media"><img src={IMG.mining} alt="Andes mining infrastructure" loading="lazy" /></div>
              <div className="sector-card__body">
                <span className="tag">{t("sectors.mining.tag")}</span>
                <h3>{t("sectors.mining.title")}</h3>
                <p>{t("sectors.mining.text")}</p>
              </div>
            </article>
            <article className="sector-card">
              <div className="sector-card__media"><img src={IMG.dashboard} alt="Analytics dashboard" loading="lazy" /></div>
              <div className="sector-card__body">
                <span className="tag">{t("sectors.macro.tag")}</span>
                <h3>{t("sectors.macro.title")}</h3>
                <p>{t("sectors.macro.text")}</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section" id="process">
        <div className="container">
          <div className="section__header">
            <span className="eyebrow">{t("nav.process")}</span>
            <h2>{t("process.title")}</h2>
            <p>{t("process.lead")}</p>
          </div>
          <ol className="process-list">
            {[
              { key: "one", icon: <Search size={18} /> },
              { key: "two", icon: <Target size={18} /> },
              { key: "three", icon: <LineChart size={18} /> },
              { key: "four", icon: <ClipboardCheck size={18} /> },
            ].map(({ key, icon }, idx) => (
              <li key={key}>
                <div className="process-list__step">
                  <span className="process-list__num">{String(idx + 1).padStart(2, "0")}</span>
                  <span className="process-list__icon" aria-hidden="true">{icon}</span>
                </div>
                <div className="process-list__body">
                  <h3>{t(`process.steps.${key}.title`)}</h3>
                  <p>{t(`process.steps.${key}.text`)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section className="section section--dark" id="transparency">
        <div className="container transparency">
          <div className="transparency__media">
            <img src={IMG.city} alt="Buenos Aires financial district" loading="lazy" />
          </div>
          <div className="transparency__copy">
            <span className="eyebrow eyebrow--light">{t("transparency.eyebrow")}</span>
            <h2>{t("transparency.title")}</h2>
            <p>{t("transparency.p1")}</p>
            <p>{t("transparency.p2")}</p>
            <ul className="transparency__list">
              <li><CheckCircle2 size={18} aria-hidden="true" /><span>{t("transparency.bullets.b1")}</span></li>
              <li><CheckCircle2 size={18} aria-hidden="true" /><span>{t("transparency.bullets.b2")}</span></li>
              <li><CheckCircle2 size={18} aria-hidden="true" /><span>{t("transparency.bullets.b3")}</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="section" id="contact">
        <div className="container contact">
          <div className="contact__copy">
            <span className="eyebrow">{t("contact.eyebrow")}</span>
            <h2>{t("contact.title")}</h2>
            <p>{t("contact.lead")}</p>
            <ul className="contact__points">
              <li>→ <span>{t("footer.emailAddress")}</span></li>
              <li>→ <span>{t("footer.phone")}</span></li>
              <li>→ <span>{t("footer.address")}</span></li>
            </ul>
          </div>
          <div className="contact__form">
            <ContactForm />
          </div>
        </div>
      </section>
    </main>
  );
}
